/**
 * HeroBackdrop — animated, glowing geometric-node network on a deep slate/navy gradient.
 *
 * Pure SVG + CSS. No image asset. No raster artifacts. Plenty of negative space on the LEFT
 * so the consumer can overlay headline text there. Animated subtly via CSS keyframes.
 *
 * Usage:
 *   <div className="relative">
 *     <HeroBackdrop />
 *     <div className="relative z-10 p-12">…headline…</div>
 *   </div>
 */
const NODES = [
  // {x, y, r, delay-s}   coordinates are within viewBox 1200x600, weighted to the RIGHT half
  { x: 720, y: 120, r: 4, d: 0 },
  { x: 880, y: 200, r: 6, d: 0.6 },
  { x: 1040, y: 90, r: 4, d: 1.1 },
  { x: 980, y: 360, r: 5, d: 0.3 },
  { x: 800, y: 440, r: 4, d: 1.4 },
  { x: 1120, y: 310, r: 6, d: 0.9 },
  { x: 660, y: 320, r: 5, d: 1.7 },
  { x: 1140, y: 480, r: 4, d: 0.2 },
];

const EDGES = [
  [0, 1], [1, 2], [1, 3], [3, 5], [3, 4], [5, 2], [4, 6], [6, 3], [5, 7], [4, 7],
];

const HeroBackdrop = ({ className = "" }) => (
  <div className={`absolute inset-0 overflow-hidden ${className}`} aria-hidden="true">
    {/* Base gradient — deep slate to dark navy */}
    <div
      className="absolute inset-0"
      style={{ background: "radial-gradient(ellipse at 80% 50%, #0F2A4A 0%, #0A1628 55%, #060D1A 100%)" }}
    />

    {/* Subtle dot grid for tech texture */}
    <div
      className="absolute inset-0 opacity-[0.07]"
      style={{
        backgroundImage: "radial-gradient(#7FB6FF 1px, transparent 1px)",
        backgroundSize: "32px 32px",
      }}
    />

    {/* Glowing node network — SVG */}
    <svg
      viewBox="0 0 1200 600"
      preserveAspectRatio="xMaxYMid slice"
      className="absolute inset-0 w-full h-full"
    >
      <defs>
        <filter id="cw-glow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur stdDeviation="3" result="blur" />
          <feMerge>
            <feMergeNode in="blur" />
            <feMergeNode in="SourceGraphic" />
          </feMerge>
        </filter>
        <linearGradient id="cw-edge" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#2A7BFF" stopOpacity="0.05" />
          <stop offset="50%" stopColor="#2A7BFF" stopOpacity="0.55" />
          <stop offset="100%" stopColor="#2A7BFF" stopOpacity="0.05" />
        </linearGradient>
      </defs>

      {/* Edges first (under nodes) */}
      <g stroke="url(#cw-edge)" strokeWidth="1.2" fill="none">
        {EDGES.map(([a, b], i) => (
          <line
            key={`edge-${a}-${b}`}
            x1={NODES[a].x}
            y1={NODES[a].y}
            x2={NODES[b].x}
            y2={NODES[b].y}
            style={{ animation: `cwEdge 6s ease-in-out ${(i * 0.4).toFixed(2)}s infinite` }}
          />
        ))}
      </g>

      {/* Nodes — glowing dots */}
      <g filter="url(#cw-glow)">
        {NODES.map((n, i) => (
          <circle
            key={`node-${n.x}-${n.y}`}
            cx={n.x}
            cy={n.y}
            r={n.r}
            fill="#7FB6FF"
            style={{ animation: `cwPulse 4s ease-in-out ${n.d}s infinite` }}
          />
        ))}
      </g>

      {/* A long sweeping line representing "data flowing" from left edge into the cluster */}
      <path
        d="M -50 360 C 220 280, 420 480, 660 320 S 900 200, 1120 310"
        stroke="#2A7BFF"
        strokeWidth="1.5"
        fill="none"
        opacity="0.6"
        strokeDasharray="6 8"
        style={{ animation: "cwFlow 12s linear infinite" }}
      />
    </svg>

    {/* Soft left-side darkening to guarantee text contrast */}
    <div
      className="absolute inset-y-0 left-0 w-2/3"
      style={{ background: "linear-gradient(to right, rgba(6, 13, 26, 0.85) 0%, rgba(6, 13, 26, 0.55) 40%, transparent 100%)" }}
    />

    <style>{`
      @keyframes cwPulse {
        0%, 100% { opacity: 0.45; transform: scale(1); }
        50%      { opacity: 1;    transform: scale(1.25); }
      }
      @keyframes cwEdge {
        0%, 100% { opacity: 0.35; }
        50%      { opacity: 0.9; }
      }
      @keyframes cwFlow {
        0%   { stroke-dashoffset: 0; }
        100% { stroke-dashoffset: 280; }
      }
    `}</style>
  </div>
);

export default HeroBackdrop;
